import { Space } from "antd";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import ArchiveButton from "../components/ArchiveButton";
import ReactivateButton from "../components/ReactivateButton";
import RemoveButton from "../components/RemoveButton";
import UpdateProduct from "../components/UpdateProduct";
import { customToFixed } from "utils/toFixedNumber";
import { getTranslationByLanguage } from "utils/getTranslationByLanguage";
import { TFunction } from "i18next";

interface GetColumnsParams {
  t: TFunction;
  categoryOptions: any[];
  refetch: () => void;
  invoiceLanguage?: string;
}

export const getProductColumns = ({
  t,
  categoryOptions,
  refetch,
  invoiceLanguage,
}: GetColumnsParams) => [
  {
    title: t("Name"),
    dataIndex: "name",
    key: "name",
    filterType: {
      type: "SearchFilter",
      varName: "name_Istartswith",
    },
    render: (_: any, record: any) => {
      const nameTranslation = getTranslationByLanguage(record?.nameTranslations, invoiceLanguage);
      const fallback = record?.nameTranslations?.find((item: any) => item?.value)?.value;
      return nameTranslation?.value || fallback || record?.name;
    },
  },
  {
    title: t("Reference"),
    dataIndex: "reference",
    key: "reference",
    filterType: {
      type: "SearchFilter",
      varName: "reference_Istartswith",
    },
  },
  {
    title: t("Category"),
    dataIndex: ["category", "name"],
    key: "category",
    filterType: {
      type: "SelectFilter",
      varName: "category",
      options: categoryOptions,
    },
  },
  {
    title: t("Price HT"),
    key: "priceHT",
    align: "right" as const,
    render: (_: any, record: any) => {
      const main =
        record?.prices?.find(
          (p: ListProductsData["prices"][number]) => p?.currency?.isMainCurrency,
        ) ?? record?.prices?.[0];
      const net = Number(main?.currency?.priceNet?.net ?? 0);
      const decimals = main?.currency?.decimalPlaces ?? 2;
      return `${customToFixed(net, decimals)} ${main?.currency?.code ?? ""}`.trim();
    },
  },
  {
    title: t("Price TTC"),
    key: "priceTTC",
    align: "right" as const,
    render: (_: any, record: any) => {
      const main =
        record?.prices?.find(
          (p: ListProductsData["prices"][number]) => p?.currency?.isMainCurrency,
        ) ?? record?.prices?.[0];
      const net = Number(main?.currency?.priceNet?.net ?? 0);
      const rate = Number(record?.vat?.rate ?? 0);
      const decimals = main?.currency?.decimalPlaces ?? 2;
      const ttc = net + net * rate;
      return `${customToFixed(ttc, decimals)} ${main?.currency?.code ?? ""}`.trim();
    },
  },
  {
    key: "archivedAt",
    title: t("Archived at"),
    render: (_: any, record: any) =>
      record?.archivedAt && dayjs(record.archivedAt).format(DATE_FORMAT),
  },
  {
    dataIndex: t("Action"),
    key: "action",
    title: "Action",
    align: "center" as const,
    width: "100px",
    fixed: "right" as const,
    render: (_: any, record: any) => <ProductTableActions record={record} refetch={refetch} />,
  },
];

interface ProductTableActionsProps {
  record: any;
  refetch: () => void;
}

function ProductTableActions({ record, refetch }: ProductTableActionsProps) {
  return (
    <Space size="small" direction="horizontal" className="centered">
      <UpdateProduct id={record.id} refetchProducts={refetch} />
      {record?.canBeDeleted && <RemoveButton id={record.id} refetch={refetch} />}
      {!record.canBeDeleted && !record?.archivedAt && (
        <ArchiveButton id={record.id} refetch={refetch} />
      )}
      {!record.canBeDeleted && record?.archivedAt && (
        <ReactivateButton id={record.id} refetch={refetch} />
      )}
    </Space>
  );
}
